import type { ColumnDef, ColumnSizingState } from '@tanstack/react-table'
import type { Product } from '@/types'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { CheckCircle2, XCircle, AlertCircle } from 'lucide-react'

const getActiveStatusIcon = (isActive?: boolean) => {
  return isActive ? (
    <CheckCircle2 className="h-4 w-4 text-success" />
  ) : (
    <XCircle className="h-4 w-4 text-destructive" />
  )
}

interface ProductsColumnsProps {
  onViewProduct: (productId: string, name: string) => void
  onEditProduct?: (productId: string, name: string) => void
  onDeleteProduct: (productId: string, name: string) => void
  canEdit?: boolean
  selectedIds?: Set<string>
  onToggleSelect?: (id: string) => void
  onToggleSelectAll?: () => void
  allSelected?: boolean
}

export const createProductsColumns = ({
  onViewProduct,
  onEditProduct,
  onDeleteProduct,
  canEdit,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  allSelected,
}: ProductsColumnsProps): ColumnDef<Product>[] => [
  {
    id: 'select',
    header: () => (
      <Checkbox
        checked={allSelected}
        onCheckedChange={onToggleSelectAll}
        aria-label="Select all products"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={selectedIds?.has(row.original.id) ?? false}
        onCheckedChange={() => onToggleSelect?.(row.original.id)}
        aria-label={`Select product ${row.original.name}`}
      />
    ),
    size: 50,
    minSize: 50,
    maxSize: 50,
    enableResizing: false,
  },
  {
    id: 'active_status',
    header: 'Status',
    accessorKey: 'is_active',
    size: 80,
    cell: ({ row }) => (
      <div className="flex items-center">
        {getActiveStatusIcon(row.original.is_active)}
        <span className="sr-only">{row.original.is_active ? 'Active' : 'Inactive'}</span>
      </div>
    ),
  },
  {
    id: 'name',
    header: 'Product Name',
    accessorKey: 'name',
    meta: { sortField: 'name' },
    size: 300,
    cell: ({ row }) => (
      <Button
        variant="link"
        className="h-auto p-0 text-primary hover:underline font-medium"
        onClick={() => onViewProduct(row.original.id, row.original.name)}
        aria-label={`View details for product ${row.original.name}`}
      >
        {row.original.name}
      </Button>
    ),
  },
  {
    id: 'product_code',
    header: 'Product Code',
    accessorKey: 'product_code',
    meta: { sortField: 'product_code' },
    size: 150,
    cell: ({ row }) => row.original.product_code || '-',
  },
  {
    id: 'family',
    header: 'Family',
    accessorKey: 'family',
    meta: { sortField: 'family' },
    size: 150,
    cell: ({ row }) => row.original.family || '-',
  },
  {
    id: 'priced_in',
    header: 'Priced In',
    accessorKey: 'priced_currencies',
    size: 150,
    // Currency CODES, not an amount.
    //
    // This column used to show a single "Price". A product does not have one
    // price — it has a price per currency (4.1 of them on average here) — so
    // the figure shown was whichever one the API resolved, in whichever
    // currency the caller asked for. This page asks for none, so it fell back
    // to CAD and would now read "No price" for 62 of 70 products that are
    // perfectly well priced in USD.
    //
    // The catalogue's job is to say whether a thing exists and is sellable.
    // "Priced in USD, AUD" answers that without being able to mislabel
    // anything. The amounts live in the price list, which shows every currency
    // side by side and is the only screen that can show them honestly.
    cell: ({ row }) => {
      const codes = row.original.priced_currencies ?? []
      if (codes.length === 0) {
        return (
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-warning" aria-hidden="true" />
            <span className="text-muted-foreground">No selling price</span>
          </div>
        )
      }
      return <span aria-label={`Priced in ${codes.join(', ')}`}>{codes.join(', ')}</span>
    },
  },
  {
    id: 'actions',
    header: () => <div className="text-right">Actions</div>,
    size: 160,
    minSize: 140,
    maxSize: 200,
    enableResizing: false, // Actions column doesn't need resizing
    cell: ({ row }) => (
      <div className="flex justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          aria-label={`View product ${row.original.name}`}
          onClick={() => onViewProduct(row.original.id, row.original.name)}
        >
          View
        </Button>
        {canEdit && onEditProduct && (
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Edit product ${row.original.name}`}
            onClick={() => onEditProduct(row.original.id, row.original.name)}
          >
            Edit
          </Button>
        )}
        {canEdit && (
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Delete product ${row.original.name}`}
            onClick={() => onDeleteProduct(row.original.id, row.original.name)}
          >
            Delete
          </Button>
        )}
      </div>
    ),
  },
]

export const productsDefaultColumnSizing: ColumnSizingState = {
  select: 50,
  active_status: 80,
  name: 300,
  product_code: 150,
  family: 150,
  priced_in: 150,
  actions: 160,
}
